import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Ticket,
  Plus,
  BarChart3,
  TestTube,
  Calendar,
  Users,
  Percent
} from 'lucide-react';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Filter } from '../../components/ui/Filter';
import { Pagination } from '../../components/ui/Pagination';
import { 
  addCoupon, 
  updateCoupon, 
  deleteCoupon, 
  toggleCouponStatus,
  validateCoupon,
  COUPON_TYPE,
  COUPON_STATUS 
} from '../../store/couponSlice';
import { FormLayout } from '../../components/Layout/FormLayout';
import Heading from '../../components/heading';

const PAGE_SIZE = 10;

export function CouponManagementPage() {
  const dispatch = useDispatch();
  const coupons = useSelector(state => state.coupons);
  
  const [activeTab, setActiveTab] = useState('coupons');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [testCouponCode, setTestCouponCode] = useState('');
  const [testResult, setTestResult] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: COUPON_TYPE.PERCENTAGE,
    value: '',
    maxDiscount: '',
    minOrderValue: '',
    usageLimit: '',
    userUsageLimit: '',
    validFrom: '',
    validUntil: '',
    applicableToNewUsers: true,
    applicableToExistingUsers: true,
    applicableRideTypes: ['standard']
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case COUPON_STATUS.ACTIVE: return 'success';
      case COUPON_STATUS.EXPIRED: return 'warning';
      case COUPON_STATUS.USED_UP: return 'info';
      case COUPON_STATUS.INACTIVE: return 'default';
      default: return 'default';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCoupon) {
      dispatch(updateCoupon({ id: editingCoupon.id, ...formData }));
    } else {
      dispatch(addCoupon(formData));
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      type: COUPON_TYPE.PERCENTAGE,
      value: '',
      maxDiscount: '',
      minOrderValue: '',
      usageLimit: '',
      userUsageLimit: '',
      validFrom: '',
      validUntil: '',
      applicableToNewUsers: true,
      applicableToExistingUsers: true,
      applicableRideTypes: ['standard']
    });
    setEditingCoupon(null);
    setShowForm(false);
  };

  const handleEdit = (coupon) => {
    setFormData({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description,
      type: coupon.type,
      value: coupon.value.toString(),
      maxDiscount: coupon.maxDiscount?.toString() || '',
      minOrderValue: coupon.minOrderValue?.toString() || '',
      usageLimit: coupon.usageLimit?.toString() || '',
      userUsageLimit: coupon.userUsageLimit?.toString() || '',
      validFrom: coupon.validFrom?.split('T')[0] || '',
      validUntil: coupon.validUntil?.split('T')[0] || '',
      applicableToNewUsers: coupon.applicableToNewUsers,
      applicableToExistingUsers: coupon.applicableToExistingUsers,
      applicableRideTypes: coupon.applicableRideTypes || ['standard']
    });
    setEditingCoupon(coupon);
    setShowForm(true);
  };

  const handleDelete = (coupon) => {
    if (window.confirm(`Are you sure you want to delete coupon "${coupon.code}"?`)) {
      dispatch(deleteCoupon(coupon.id));
    }
  };

  const handleToggleStatus = (coupon) => {
    dispatch(toggleCouponStatus({ id: coupon.id }));
  };

  const handleTestCoupon = () => {
    if (testCouponCode.trim()) {
      const result = dispatch(validateCoupon({
        code: testCouponCode,
        userId: 'test-user',
        rideType: 'standard',
        orderValue: 25.00
      }));
      setTestResult(result);
    }
  };

  const couponColumns = [
    {
      key: 'code',
      header: 'Coupon Code',
      primary: true,
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{item.name}</div>
        </div>
      )
    },
    {
      key: 'type',
      header: 'Type',
      render: (value, item) => (
        <div>
          <div className="capitalize font-medium">{value.replace('_', ' ')}</div>
          <div className="text-sm text-gray-500">
            {value === COUPON_TYPE.PERCENTAGE ? `${item.value}%` : 
             value === COUPON_TYPE.FIXED_AMOUNT ? formatCurrency(item.value) :
             formatCurrency(item.value)}
          </div>
        </div>
      )
    },
    {
      key: 'usedCount',
      header: 'Usage',
      render: (value, item) => (
        <div>
          <div className="font-medium">{value} / {item.usageLimit}</div>
          <div className="text-sm text-gray-500">
            {((value / item.usageLimit) * 100).toFixed(1)}% used
          </div>
        </div>
      )
    },
    {
      key: 'validUntil',
      header: 'Expires',
      render: (value) => formatDate(value)
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <StatusBadge 
          status={value} 
          variant={getStatusVariant(value)} 
        />
      )
    }
  ];

  const couponActions = [
    {
      type: 'button',
      onClick: handleEdit,
      label: 'Edit',
      variant: 'primary'
    },
    {
      type: 'button',
      onClick: handleToggleStatus,
      label: (item) => item.status === COUPON_STATUS.ACTIVE ? 'Deactivate' : 'Activate',
      variant: 'secondary'
    },
    {
      type: 'button',
      onClick: handleDelete,
      label: 'Delete',
      variant: 'danger'
    }
  ];

  const filteredCoupons = coupons.filter(coupon => {
    const matchesSearch = coupon.code.toLowerCase().includes(filter.toLowerCase()) ||
                         coupon.name.toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = statusFilter === 'all' || coupon.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredCoupons.length / PAGE_SIZE);
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filteredCoupons.slice(start, start + PAGE_SIZE);

  const tabs = [
    { id: 'coupons', label: 'Coupons', icon: Ticket },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'test', label: 'Test Coupon', icon: TestTube }
  ];

  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter(c => c.status === COUPON_STATUS.ACTIVE).length;
  const totalUsage = coupons.reduce((sum, c) => sum + c.usedCount, 0);
  const totalSavings = coupons.reduce((sum, c) => {
    if (c.type === COUPON_TYPE.PERCENTAGE) {
      return sum + (c.usedCount * c.maxDiscount);
    }
    return sum + (c.usedCount * c.value);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Coupon Management</h1>
         
          <p className="mt-2 text-gray-600">
            Manage promotional codes and discount campaigns
          </p>
        </div>
        <Button 
          variant="primary" 
          className="mt-4 sm:mt-0"
          onClick={() => setShowForm(true)}
        >
          <Plus size={16} className="mr-2" />
          Create Coupon
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Ticket className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Coupons</p>
              <p className="text-2xl font-bold text-gray-900">{totalCoupons}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Coupons</p>
              <p className="text-2xl font-bold text-gray-900">{activeCoupons}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Usage</p>
              <p className="text-2xl font-bold text-gray-900">{totalUsage.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Percent className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Savings</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSavings)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'coupons' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
             <Filter
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by code or name..."
              />
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={['all', ...Object.values(COUPON_STATUS)]}
              className="w-full sm:w-auto"
            />
          </div>

          {/* Coupon Form */}
          {showForm && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 
                  <Input
                    label="Coupon Code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="SAVE20"
                    required
                  />
                  <Input
                    label="Name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="20% Off Promotion"
                    required
                  />
                </div>
                <Input
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Get 20% off your next ride"
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Select
                    label="Type"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    options={Object.values(COUPON_TYPE)}
                  />
                  <Input
                    label="Value"
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                    placeholder={formData.type === COUPON_TYPE.PERCENTAGE ? "20" : "5.00"}
                    required
                  />
                  <Input
                    label="Max Discount"
                    type="number"
                    value={formData.maxDiscount}
                    onChange={(e) => setFormData({...formData, maxDiscount: e.target.value})}
                    placeholder="10.00"
                  />
                </div>
                <div className="flex gap-4">
                  <Button type="submit" variant="primary">
                    {editingCoupon ? 'Update' : 'Create'} Coupon
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Coupons Table */}
          <ResponsiveTable
            data={pageItems}
            columns={couponColumns}
            actions={couponActions}
          />

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {activeTab === 'test' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Coupon</h2>
          <div className="flex gap-4 mb-4">
            <Input
              label="Coupon Code"
              value={testCouponCode}
              onChange={(e) => setTestCouponCode(e.target.value)}
              placeholder="Enter coupon code to test"
            />
            <div className="flex items-end">
              <Button onClick={handleTestCoupon} variant="primary">
                <TestTube size={16} className="mr-2" />
                Test
              </Button>
            </div>
          </div>
          {testResult && (
            <div className={`p-4 rounded-lg ${testResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className={`font-medium ${testResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.valid ? 'Valid Coupon' : 'Invalid Coupon'}
              </div>
              {testResult.error && (
                <div className="text-red-600 text-sm mt-1">{testResult.error}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
